import { isObservablePlugin, isObserverPlugin, isOperatorPlugin, isTaskConfiguration } from "../../plugin/Plugin";
import { split } from "../../stream/operator/split";
import { merge } from "../../stream/operator/merge";
import { task } from "../../stream/operator/task";
import { AbstractVisitor } from "./Visitor";
/**
 * Visitor implementation to execute Plugins
 *
 * @public
 */
export class PluginExecutionVisitor extends AbstractVisitor {
    constructor(threadPool, registry) {
        super();
        this.skipVisitPlugin = false;
        this.threadPool = threadPool;
        this.registry = registry;
        this.node = document.body;
    }
    init(node) {
        this.node = node;
        this.skipVisitPlugin = false;
        this.mergeObservables = undefined;
    }
    visitPlugin(plugin) {
        if (this.skipVisitPlugin) {
            this.skipVisitPlugin = false;
            return;
        }
        if (plugin.merge) {
            this.mergeObservables = [];
        }
        let stream = this.execute(plugin.plugin, plugin.parameter);
        if (isTaskConfiguration(stream)) {
            stream = task(this.threadPool, stream);
        }
        if (isObservablePlugin(stream) || stream === undefined) {
            plugin.stream = stream;
            // save stream reference for merging, if merge Observables is defined
            if (this.mergeObservables && stream) {
                this.mergeObservables.push(stream);
            }
        }
        else {
            throw new Error(`Plugin ${plugin.plugin} should be from type Observable or Void`);
        }
    }
    visitPipe(prevPlugin, nextPlugin) {
        // prev plugin must already contain its stream instance and to pipe the next plugin it must be from type Observable or Operator
        if (!prevPlugin.stream ||
            !(isObservablePlugin(prevPlugin.stream) ||
                isOperatorPlugin(prevPlugin.stream))) {
            throw new Error("Failed to build Stream from Plugin Definition.");
        }
        let stream = this.execute(nextPlugin.plugin, nextPlugin.parameter);
        if (isTaskConfiguration(stream)) {
            stream = task(this.threadPool, stream);
        }
        nextPlugin.stream = stream;
        if (isOperatorPlugin(stream)) {
            prevPlugin.stream.pipe(stream);
            // replace latest merge observable with current stream instance
            if (this.mergeObservables) {
                this.mergeObservables[this.mergeObservables.length - 1] = stream;
            }
        }
        else if (isObserverPlugin(stream)) {
            prevPlugin.stream.subscribe(stream);
        }
        else {
            throw new Error(`Failed to pipe plugin ${prevPlugin.plugin} into ${nextPlugin.plugin} (${prevPlugin.plugin} | ${nextPlugin.plugin} ).
      Make sure ${nextPlugin.plugin} is from Type Operator or Observer/Subscriber.`);
        }
        // we handle plugin execution already, so the next visitPlugin can be "skipped"
        this.skipVisitPlugin = true;
    }
    visitMerge(plugin) {
        if (!this.mergeObservables) {
            throw new Error("No Plugins to merge are defined");
        }
        const merged = merge(...this.mergeObservables);
        const stream = this.execute(plugin.plugin, plugin.parameter);
        // handle TaskConfiguration Type @see #visitPipe()
        if (isOperatorPlugin(stream)) {
            merged.pipe(stream);
        }
        else if (isObserverPlugin(stream)) {
            merged.subscribe(stream);
        }
        else {
            throw new Error(`Failed to merge plugins into ${plugin.plugin}.
      Make sure ${plugin.plugin} is from Type Operator or Observer/Subscriber.`);
        }
        // we handle plugin execution already, so the next visitPlugin can be "skipped"
        this.skipVisitPlugin = true;
        this.mergeObservables = undefined;
    }
    visitGroupStart(pluginGroup) {
        if (pluginGroup.merge) {
            this.mergeObservables = [];
        }
    }
    visitSplitStart(plugins, splitSource) {
        if (!splitSource.stream ||
            !(isObservablePlugin(splitSource.stream) ||
                isOperatorPlugin(splitSource.stream))) {
            throw new Error(`Unable to split stream after plugin ${splitSource.plugin}. Plugin must be from type Obervable or Operator.`);
        }
        // manipulate the plugin stream to the special split operator
        splitSource.stream = splitSource.stream.pipe(split());
        this.visitPipe(splitSource, plugins[0]);
    }
    visitSplitNext(plugins, prevPlugin, nextPlugin, splitSource) {
        this.visitPipe(splitSource, nextPlugin);
    }
    visitPluginDefinition(pluginDefinition) {
        // reset state for every new plugin definition
        this.init(this.node);
    }
    execute(name, parameter = {}) {
        const factory = this.registry.getOrThrow(name);
        return factory(this.node, parameter);
    }
}
//# sourceMappingURL=PluginExecutionVisitor.js.map